/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'

import {
  useFilterStore,
  useMapStore,
  usePropertiesStore
} from '@wiggot/search-components/state-library/src'
import { getAssociations } from '@/services/search.services'
import { setNewQueryParams } from '@/utils/routes'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider/AmplitudeProvider'
import { getMacrobolsaUser } from '@wiggot/apis-ssr/utils/macrobolsa/get-macrobolsa-user-status-client'
import { MACROBOLSA_ASSOCIATION_ID } from '@wiggot/web-components/constants/associations'
import { AmplitudeEvents } from '@/interfaces/enum/amplitudeEvents.enum'
import {
  ESearchLevel,
  EOpportunityCriteria,
  EPropertyType
} from '@wiggot/search-components/types-library/src'
import { IAssociation } from '@/interfaces/search.interface'
import { ILocation } from '@wiggot/search-components/state-library/src/stores/filterStore/types'
import toast from '@dd3tech/wiggui/Toast'

// EPropertyType local se elimino: setPropertyType (state-library) exige el
// EPropertyType de types-library, que es un enum distinto (mismos valores,
// pero nominalmente incompatible en TS). Se usa el de types-library en todo
// este archivo; ahi el valor de renta se llama RENT en vez de LEASE.
export enum EConstructionType {
  'HOUSE' = 'house',
  'APARTMENT' = 'apartment',
  'OTHER' = 'other',
  NONE = 0
}

interface IPrices {
  priceFrom?: number
  priceTo?: number
}

export interface IFilters {
  associations: string[]
  operation_type: string[]
  property_type: string[]
  prices: IPrices
  bedrooms: number
  bathrooms: number
  parkings: number
  amenities: string[]
  opportunity_criteria: string | undefined
  commission_rate_range: number
  surface_from: number
  surface_to: number
  showOnlyCommissionProperties: boolean
  wiggotUserType?: 'full' | 'macrobolsa'
  searchLevel?: ESearchLevel
  latitude: number
  longitude: number
  searchAddress?: string
  locations: ILocation[]
}

const OPERATION_TYPE: { [key: string]: string } = {
  SALE: 'SALE',
  LEASE: 'LEASE',
  PRESALE: 'PRESALE'
}

const initialFilters: IFilters = {
  associations: [],
  operation_type: ['SALE'],
  property_type: [],
  prices: {
    priceFrom: undefined,
    priceTo: undefined
  },
  bedrooms: 1,
  bathrooms: 1,
  parkings: 0,
  amenities: [],
  opportunity_criteria: undefined,
  searchAddress: undefined,
  commission_rate_range: 0,
  surface_from: 0,
  surface_to: 0,
  latitude: 0,
  longitude: 0,
  showOnlyCommissionProperties: false,
  locations: []
}

const MORE_FILTERS_KEYS = [
  { isArray: true, name: 'amenities' },
  { isArray: false, name: 'bedrooms' },
  { isArray: false, name: 'bathrooms' },
  { isArray: false, name: 'parkings' }
]

interface IProps {
  setShowFirtsSearch: (show: boolean) => void
  hasCleanFilters: boolean
  setHasCleanFilters: (hasCleanFilters: boolean) => void
}
export const useFilters = ({
  setShowFirtsSearch,
  hasCleanFilters,
  setHasCleanFilters
}: IProps) => {
  const {
    constructionType,
    setConstructionType,
    propertyType,
    setPropertyType,
    setPriceFrom,
    setPriceTo,
    setRooms,
    setBathrooms,
    setParkings,
    setAmenities,
    setLatitude,
    setLongitude,
    setSearchAddress,
    setOpportunityCriteria,
    setSearchLevel,
    setAssociationIds,
    setCommissionRateLowRange,
    setCommissionRateHighRange,
    setSurfaceFrom,
    setSurfaceTo,
    setAreaBounds,
    setPamRequestId,
    setShowOnlyCommissionProperties,
    setLocations,
    setWiggotUserType,
    rooms,
    bathrooms,
    parkings,
    amenities,
    opportunityCriteria,
    priceFrom,
    priceTo,
    associationIds,
    surfaceFrom,
    surfaceTo,
    searchAddress,
    latitude,
    longitude,
    searchLevel,
    locations
  } = useFilterStore()
  const { hasMacrobolsaNotAdvancedUser } = getMacrobolsaUser()
  const { setPropertyLoaderStatus, setFirtsTime } = usePropertiesStore()
  const { setDrawingTool } = useMapStore()
  const { trackEvent, isInitialized } = useAmplitude()
  const [autocompleteValue, setAutocompleteValue] = useState(
    '¿Dónde quieres buscar?'
  )
  const [associations, setAssociations] = useState<IAssociation[]>([])
  const [isLoadingPreviousFilters] = useState(false)
  const [filters, setFilters] = useState<IFilters>({
    ...initialFilters,
    bedrooms: rooms || initialFilters?.bedrooms,
    bathrooms: bathrooms || initialFilters?.bathrooms,
    parkings:
      parkings ||
      (propertyType?.includes('apartment') ||
      propertyType?.includes('office') ||
      propertyType?.includes('consulting_room')
        ? 0
        : initialFilters?.parkings),
    amenities: amenities || initialFilters?.amenities,
    surface_from: surfaceFrom || initialFilters?.surface_from,
    surface_to: surfaceTo || initialFilters?.surface_to,
    latitude,
    longitude,
    locations,
    searchLevel,
    searchAddress,
    showOnlyCommissionProperties: Boolean(hasMacrobolsaNotAdvancedUser),
    associations: hasMacrobolsaNotAdvancedUser
      ? [MACROBOLSA_ASSOCIATION_ID?.toString()]
      : initialFilters?.associations || []
  })

  const [moreFiltersCounter, setMoreFiltersCounter] = useState(0)
  const isLoaded = false
  const onChangeLocation = ({
    id,
    latitude,
    longitude,
    searchLevel,
    searchAddress
  }: {
    id: string
    latitude: number
    longitude: number
    searchLevel: ESearchLevel
    searchAddress: string
  }) => {
    setDrawingTool({
      isActive: false,
      status: undefined
    })
    setPamRequestId(undefined)
    const newLocations = filters.locations.filter(
      (location) => location.id !== id
    )
    const isMunicipalityOrState = [
      ESearchLevel.MUNICIPALITY,
      ESearchLevel.STATE
    ].includes(searchLevel)
    if (
      filters.locations.length > 0 &&
      isMunicipalityOrState &&
      searchAddress
    ) {
      toast({
        type: 'error',
        title: 'No puedes agregar un municipio o ciudad a una búsqueda múltiple'
      })
      return
    } else if (isMunicipalityOrState && searchAddress) {
      setFilters((prev: IFilters) => ({
        ...prev,
        latitude: latitude || 0,
        longitude: longitude || 0,
        searchLevel: searchLevel || undefined,
        searchAddress: searchAddress || '',
        locations: []
      }))
      setAutocompleteValue(searchAddress)
      return
    }
    setFilters((prev: IFilters) => ({
      ...prev,
      latitude: latitude || 0,
      longitude: longitude || 0,
      searchLevel: searchLevel || undefined,
      searchAddress: searchAddress || '',
      locations: searchAddress
        ? [
            ...newLocations,
            { id, latitude, longitude, searchLevel, searchAddress }
          ]
        : filters.locations
    }))
    setAutocompleteValue(searchAddress)
  }
  const onDeleteLocation = (id: string) => {
    setFilters((prev: IFilters) => ({
      ...prev,
      locations: filters.locations.filter((location) => location.id !== id)
    }))
  }
  const onClose = (cb: () => void) => {
    if (cb) {
      cb()
    }
  }

  const onChangeOperationType = (value: string) => {
    setFilters((prev: IFilters) => ({
      ...prev,
      operation_type: [value],
      prices: initialFilters?.prices
    }))
    // setNewQueryParams({ operation: value, ...initialFilters?.prices })
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_OPERATIONTYPE_ACTION,
        eventProperties: {
          operationType: value
        }
      })
    }
  }

  const onChangePropertyType = (value: { property_type: string[] }) => {
    setFilters((prev: IFilters) => ({
      ...prev,
      property_type: value?.property_type || []
    }))
  }

  const onChangeAssociations = (value: string[]) => {
    let newAssociations = value
    if (hasMacrobolsaNotAdvancedUser) {
      if (
        value?.length > 1 &&
        newAssociations.includes(MACROBOLSA_ASSOCIATION_ID)
      ) {
        newAssociations = newAssociations.filter(
          (item) => item !== MACROBOLSA_ASSOCIATION_ID
        )
      }
      if (newAssociations.length === 0) {
        newAssociations = [MACROBOLSA_ASSOCIATION_ID]
      }
      setFilters((prev: IFilters) => ({
        ...prev,
        associations: newAssociations || []
      }))
      return
    }
    setFilters((prev: IFilters) => ({
      ...prev,
      associations: value || []
    }))
  }

  const onApplyAssociations = (cb?: () => void) => {
    if (filters?.associations) {
      const assocIds = filters?.associations?.join(',')
      setAssociationIds(assocIds ? assocIds : undefined)
    }
    if (cb) {
      cb()
    }
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_ASSOCIATIONS_ACTION
      })
    }
  }

  const onApplyPropertyTypes = (cb?: () => void) => {
    const typesLength = filters?.property_type?.length

    if (typesLength === 0) {
      setConstructionType(undefined)
      setNewQueryParams({ constructionType: '' })
    }
    if (typesLength > 0) {
      const types = filters?.property_type
        ?.map((type) => type?.toLowerCase())
        ?.join(',')
      setConstructionType(types)
      setNewQueryParams({ constructionType: types })
    }
    if (cb) {
      cb()
    }
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_PROPERTYTYPES_ACTION,
        eventProperties: {
          propertyTypes: filters?.property_type
        }
      })
    }
  }

  const onChangePrices = (price: IPrices) => {
    if (
      (price?.priceFrom === 0 || !price?.priceFrom) &&
      Boolean(price?.priceTo) &&
      price?.priceTo &&
      price?.priceTo > 0
    ) {
      setPriceFrom(1)
    } else {
      setPriceFrom(price?.priceFrom || 0)
    }

    setPriceTo(price?.priceTo || 0)
    setNewQueryParams({ priceTo: price?.priceTo, priceFrom: price?.priceFrom })
    setFilters((prev: IFilters) => ({
      ...prev,
      prices: { priceFrom: price?.priceFrom, priceTo: price?.priceTo }
    }))
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_PRICES_ACTION
      })
    }
  }
  const onConfirm = (cb: () => void) => {
    if (cb) {
      cb()
    }
  }
  const onInputPricesChange = ({
    key,
    value
  }: {
    key: string
    value: number
  }) => {
    setFilters((prev: IFilters) => ({
      ...prev,
      prices: { ...prev.prices, [key]: value }
    }))
  }
  const resetMoreFilters = () => {
    setFilters((prev: IFilters) => ({
      ...prev,
      bedrooms: initialFilters?.bedrooms,
      bathrooms: initialFilters?.bathrooms,
      parkings: initialFilters?.parkings,
      amenities: initialFilters?.amenities,
      surface_from: initialFilters?.surface_from,
      surface_to: initialFilters?.surface_to,
      showOnlyCommissionProperties:
        initialFilters?.showOnlyCommissionProperties,
      associations: hasMacrobolsaNotAdvancedUser
        ? [MACROBOLSA_ASSOCIATION_ID?.toString()]
        : initialFilters?.associations
    }))
  }

  const handleCommissionRange = (value: number | undefined) => {
    if (value && value > 0) {
      setCommissionRateLowRange(value)
      setCommissionRateHighRange(100)
    } else {
      setCommissionRateLowRange(0)
      setCommissionRateHighRange(0)
    }
  }

  const applyMoreFilters = () => {
    setRooms(filters?.bedrooms)
    setBathrooms(filters?.bathrooms)
    setParkings(filters?.parkings)
    setAmenities(filters?.amenities)
    handleCommissionRange(filters?.commission_rate_range)
    setSurfaceFrom(filters?.surface_from ? filters?.surface_from : undefined)
    setSurfaceTo(filters?.surface_to ? filters?.surface_to : undefined)
    setShowOnlyCommissionProperties(filters.showOnlyCommissionProperties)
    onApplyAssociations()
  }

  const handleOpportunity = (opportunity: string) => {
    // Los ids que llegan aqui vienen de OPPORTUNITIES (utils/constants.ts),
    // cuyos valores siempre coinciden 1 a 1 con EOpportunityCriteria, pero
    // esa lista esta tipada como string suelto (IOption.id: string) y no
    // contra el enum, de ahi el cast.
    setOpportunityCriteria(opportunity as EOpportunityCriteria)
    setFilters((prev: IFilters) => ({
      ...prev,
      opportunity_criteria: opportunity
    }))
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_OPPORTUNITIES_ACTION,
        eventProperties: {
          opportunity
        }
      })
    }
  }

  const onSetOperationType = (operationType: string) => {
    if (
      operationType === OPERATION_TYPE.SALE ||
      operationType === OPERATION_TYPE.PRESALE
    ) {
      setPropertyType(EPropertyType.SALE)
    }
    if (operationType === OPERATION_TYPE.LEASE) {
      setPropertyType(EPropertyType.RENT)
    }
  }
  const onSearchFilters = () => {
    setShowFirtsSearch(false)
    const operationType = filters.operation_type[0]
    onSetOperationType(operationType)
    setAreaBounds([])
    setDrawingTool({
      isActive: false,
      status: undefined
    })
    setPamRequestId(undefined)
    setLatitude(filters?.latitude || 0)
    setLongitude(filters?.longitude || 0)
    setSearchLevel((filters.searchLevel as ESearchLevel) || ESearchLevel.STATE)
    setSearchAddress(filters.searchAddress || '')
    onApplyPropertyTypes()
    onChangePrices(filters.prices)
    setNewQueryParams({
      operation: filters.operation_type[0],
      ...filters.prices
    })
    setLocations(
      filters.locations
    )
    setShowOnlyCommissionProperties(filters.showOnlyCommissionProperties)
    onApplyAssociations()
  }

  // const onSetPreviusFilters = async () => {
  //   try {
  //     setIsLoadingPreviousFilters(true)
  //     const response = await getPreviousSearch()
  //     if (
  //       response.data?.lastSearch &&
  //       Object.entries(response.data?.lastSearch).length > 0
  //     ) {
  //       const { maxPrice, minPrice, operation, searchAddress, propertyType } =
  //         response.data?.lastSearch
  //       setFilters({
  //         ...filters,
  //         prices: {
  //           priceFrom: minPrice,
  //           priceTo: maxPrice
  //         },
  //         operation_type: operation ? [operation] : filters.operation_type,
  //         searchAddress: searchAddress ?? '',
  //         latitude,
  //         longitude,
  //         //verify property type object
  //         property_type: propertyType ? propertyType : filters.property_type
  //       })
  //       if (searchAddress) {
  //         setAutocompleteValue(searchAddress ?? '¿Dónde quieres buscar?')
  //       }
  //       onChangePrices({
  //         priceFrom: minPrice,
  //         priceTo: maxPrice
  //       })
  //       if (latitude) {
  //         setLatitude(latitude)
  //       }
  //       if (longitude) {
  //         setLongitude(longitude)
  //       }
  //       setSearchAddress(searchAddress ?? '')
  //       onSetOperationType(operation ?? filters.operation_type[0])
  //       return
  //     }
  //     setShowFirtsSearch(true)
  //     setPropertyLoaderStatus({
  //       loading: false,
  //       error: false,
  //       waitingAnimation: false
  //     })
  //   } catch (error) {
  //     setShowFirtsSearch(true)
  //     setPropertyLoaderStatus({
  //       loading: false,
  //       error: false,
  //       waitingAnimation: false
  //     })
  //   } finally {
  //     setIsLoadingPreviousFilters(false)
  //   }
  // }

  //for the future to use query params to set filters
  // const setFiltersFromUrl = (params: { [key: string]: any }) => {
  //   const autocomplete = params?.search
  //   const propertyTypeParam = (params && params?.constructionType) || ''
  //   const propertyType =
  //     (propertyTypeParam && propertyTypeParam?.split(',')) || []

  //   const onlySharedCommission = params?.sharesCommision === 'true'
  //   if (onlySharedCommission) {
  //     setShowOnlyCommissionProperties(onlySharedCommission)
  //   }
  //   if (autocomplete) {
  //     setAutocompleteValue(autocomplete)
  //   }
  //   if (params?.searchLevel) {
  //     setSearchLevel(params?.searchLevel)
  //   }
  //   if (params?.operation) {
  //     const operation = params?.operation
  //     setFilters((prev: IFilters) => ({
  //       ...prev,
  //       operation_type: [operation]
  //     }))
  //     if (
  //       operation === OPERATION_TYPE.SALE ||
  //       operation === OPERATION_TYPE.PRESALE
  //     ) {
  //       setPropertyType(EPropertyType.SALE)
  //     }
  //     if (operation === OPERATION_TYPE.LEASE) {
  //       setPropertyType(EPropertyType.LEASE)
  //     }
  //   }
  //   if (!params || !params?.constructionType) {
  //     setConstructionType(undefined)
  //     setFilters((prev: IFilters) => ({
  //       ...prev,
  //       property_type: []
  //     }))
  //   } else {
  //     setConstructionType(params?.constructionType)
  //     setFilters((prev: IFilters) => ({
  //       ...prev,
  //       property_type: propertyType?.map((type: string) => type?.toUpperCase())
  //     }))
  //   }
  //   if (params?.lat && params?.lng) {
  //     setLatitude(Number(params?.lat))
  //     setLongitude(Number(params?.lng))
  //   }
  //   if (params?.search) {
  //     setSearchAddress(params?.search)
  //   }
  //   if (params?.priceTo && params?.priceTo > 1) {
  //     setPriceTo(Number(params?.priceTo))
  //     setFilters((prev: IFilters) => ({
  //       ...prev,
  //       prices: { ...prev.prices, priceTo: params?.priceTo }
  //     }))
  //   }
  //   if (params?.priceFrom && params?.priceFrom > 1) {
  //     setPriceFrom(Number(params?.priceFrom))
  //     setFilters((prev: IFilters) => ({
  //       ...prev,
  //       prices: { ...prev.prices, priceFrom: params?.priceFrom }
  //     }))
  //   }
  //   if (
  //     Boolean(params?.priceTo) &&
  //     params?.priceTo > 1 &&
  //     Boolean(params?.priceFrom) &&
  //     params?.priceFrom < 2
  //   ) {
  //     setPriceFrom(1)
  //   }
  //   setIsLoaded(true)
  // }

  const handleAssociations = async () => {
    const response = await getAssociations()
    if (response?.isSuccess && response?.data && response?.data?.length > 0) {
      setAssociations(
        response?.data?.map((assoc) => ({
          value: assoc?.associationId?.toString(),
          label: assoc?.associationName
        }))
      )
    }
  }
  useEffect(() => {
    handleAssociations()
    if (window) {
      const queryParams = new URLSearchParams(window.location.search)
      const paramsObj: { [key: string]: any } = {}
      queryParams?.forEach((value, key) => {
        paramsObj[key] = value
      })
      // setFiltersFromUrl(paramsObj)
    }
  }, [])

  useEffect(() => {
    // to set previous filters
    // onSetPreviusFilters()
    setPropertyLoaderStatus({
      loading: false,
      error: false,
      waitingAnimation: false
    })
    setFirtsTime(false)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      if (!priceFrom && !priceTo && isLoaded) {
        setFilters((prev: IFilters) => ({
          ...prev,
          prices: initialFilters?.prices
        }))
      }
    }, 200)
  }, [priceFrom, priceTo, isLoaded])

  useEffect(() => {
    if (!opportunityCriteria && isLoaded && filters?.opportunity_criteria) {
      setFilters((prev: IFilters) => ({
        ...prev,
        opportunity_criteria: opportunityCriteria
      }))
    }

    if (!associationIds && isLoaded && filters?.associations?.length > 0) {
      setFilters((prev: IFilters) => ({
        ...prev,
        associations: []
      }))
    }
  }, [opportunityCriteria, associationIds, isLoaded])

  useEffect(() => {
    let newCounter = 0
    MORE_FILTERS_KEYS.forEach((key) => {
      const filter = filters[key?.name as keyof IFilters]
      if (key?.isArray && Array.isArray(filter) && filter.length > 0) {
        newCounter++
      } else if (!key?.isArray && typeof filter === 'number' && filter > 1) {
        newCounter++
      }
    })
    setMoreFiltersCounter(newCounter)
  }, [bathrooms, parkings, amenities, rooms])

  useEffect(() => {
    if (hasCleanFilters) {
      setFilters({
        ...initialFilters,
        bedrooms: rooms || initialFilters?.bedrooms,
        bathrooms: bathrooms || initialFilters?.bathrooms,
        parkings:
          parkings ||
          (constructionType?.includes('apartment') ||
          constructionType?.includes('office') ||
          constructionType?.includes('consulting_room')
            ? 0
            : initialFilters?.parkings),
        amenities: amenities || initialFilters?.amenities,
        surface_from: surfaceFrom || initialFilters?.surface_from,
        surface_to: surfaceTo || initialFilters?.surface_to,
        latitude,
        longitude,
        searchLevel,
        searchAddress
      })
      setAutocompleteValue('¿Dónde quieres buscar?')
      setHasCleanFilters(false)
    }
  }, [hasCleanFilters])

  useEffect(() => {
    setWiggotUserType?.(hasMacrobolsaNotAdvancedUser ? 'macrobolsa' : 'full')
  }, [hasMacrobolsaNotAdvancedUser])

  return {
    setConstructionType,
    setPropertyType,
    onChangeOperationType,
    onClose,
    onChangePrices,
    onInputPricesChange,
    filters,
    setFilters,
    onConfirm,
    constructionType,
    propertyType,
    associations,
    searchAddress,
    isLoadingPreviousFilters,
    setSearchAddress,
    setAutocompleteValue,
    applyMoreFilters,
    moreFiltersCounter,
    resetMoreFilters,
    onChangePropertyType,
    onApplyPropertyTypes,
    handleOpportunity,
    autocompleteValue,
    setNewQueryParams,
    onChangeAssociations,
    onApplyAssociations,
    onSearchFilters,
    onDeleteLocation,
    handleCommissionRange,
    onChangeLocation
  }
}
